<script lang="ts">
  import { themeToggleService, type ThemeMode } from '$lib/services/ThemeToggleService';
  
  interface Props {
    currentTheme: ThemeMode;
    onThemeChange: (theme: ThemeMode) => void;
  }
  
  let { currentTheme, onThemeChange }: Props = $props();
  
  function handleToggle() {
    const nextTheme = themeToggleService.getNextTheme(currentTheme);
    themeToggleService.applyTheme(nextTheme);
    onThemeChange(nextTheme);
  }
  
  let tooltip = $derived(themeToggleService.getThemeTooltip(currentTheme));
</script>

<button 
  class="theme-toggle-logo"
  onclick={handleToggle}
  title={tooltip}
  aria-label="테마 변경"
>
  {#if currentTheme === 'auto'}
    <!-- Auto mode: with day/night environment -->
    <div class="logo-wrapper-auto">
      <!-- Day/Night background environment -->
      <div class="auto-environment">
        <!-- Sun indicator for day -->
        <div class="day-indicator">
          <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="3" fill="#FFB800"/>
            <g class="sun-rays" stroke="#FFB800" stroke-width="1.5" stroke-linecap="round">
              <line x1="8" y1="2" x2="8" y2="4"/>
              <line x1="8" y1="12" x2="8" y2="14"/>
              <line x1="2" y1="8" x2="4" y2="8"/>
              <line x1="12" y1="8" x2="14" y2="8"/>
              <line x1="3.5" y1="3.5" x2="5" y2="5"/>
              <line x1="11" y1="11" x2="12.5" y2="12.5"/>
              <line x1="3.5" y1="12.5" x2="5" y2="11"/>
              <line x1="11" y1="5" x2="12.5" y2="3.5"/>
            </g>
          </svg>
        </div>
        <!-- Moon for night -->
        <svg class="night-indicator" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
          <path d="M 23 8 A 9 9 0 1 1 23 28 A 7 7 0 1 0 23 8" fill="#9CA8FF" opacity="0.9"/>
          
          <!-- Shooting stars -->
          <g class="shooting-star-1">
            <line x1="30" y1="4" x2="25" y2="9" stroke="#FFF" stroke-width="1.5" stroke-linecap="round"/>
            <circle cx="25" cy="9" r="0.8" fill="#FFF"/>
          </g>
          
          <g class="shooting-star-2">
            <line x1="15" y1="2" x2="10" y2="7" stroke="#FFF" stroke-width="1.3" stroke-linecap="round"/>
            <circle cx="10" cy="7" r="0.7" fill="#FFF"/>
          </g>
          
          <g class="shooting-star-3">
            <line x1="33" y1="12" x2="28" y2="17" stroke="#FFF" stroke-width="1.2" stroke-linecap="round"/>
            <circle cx="28" cy="17" r="0.6" fill="#FFF"/>
          </g>
          
          <g class="shooting-star-4">
            <line x1="20" y1="3" x2="15" y2="8" stroke="#FFF" stroke-width="1.4" stroke-linecap="round"/>
            <circle cx="15" cy="8" r="0.7" fill="#FFF"/>
          </g>
          
          <g class="shooting-star-5">
            <line x1="8" y1="18" x2="3" y2="23" stroke="#FFF" stroke-width="1.1" stroke-linecap="round"/>
            <circle cx="3" cy="23" r="0.5" fill="#FFF"/>
          </g>
          
          <g class="shooting-star-6">
            <line x1="32" y1="20" x2="27" y2="25" stroke="#FFF" stroke-width="1.2" stroke-linecap="round"/>
            <circle cx="27" cy="25" r="0.6" fill="#FFF"/>
          </g>
        </svg>
      </div>
      <!-- Auto mode indicator badge -->
      <div class="auto-mode-badge">
        <span class="auto-text">A</span>
      </div>
      <!-- Main logo -->
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        version="1.1" 
        viewBox="0 0 18 18"
        width="16"
        height="16"
        fill="currentColor"
        class="logo-svg"
        data-theme="auto"
      >
        <path d="M3.6,3.9c1.3,0,2.9,0,4.2,0,.7,0,2.3-.5,2.3.7,0,.3-.3.5-.6.5-2.2,0-4.6.2-6.8,0-.4,0-.7-.4-.8-.8-.2-.7,1.2-.7,1.5-.4h0ZM6.1,11c-4.2,0-3.7-5.8.7-5.2,3.7.2,3.1,5.6-.5,5.2h-.2ZM3.6,1.6c.7,0,1.5.4,2.3.4.8.1,1.6,0,2.4,0,.8,1.2-1.4,1.5-2.9,1.3-.9,0-2.7-.8-1.9-1.7h0ZM6.3,9.7c2.5,0,1.9-3.4-.6-2.8-1.2.2-1.4,1.8-.5,2.4.2.2.9.2,1,.3h0ZM4.9,13.2c-.1-1.2,1.5-.9,1.6.1.4,1.5-.2,2.3,2,2.1,1,0,6.7-.6,5,1.1-2.3.5-5.4.7-7.6-.3-.6-.8-.3-2.2-.9-3h0ZM11.3,1.1c2.6-.3,1.5,3.8,2,5,.6.4,2.6-.5,2.8.7,0,.4-.3.6-.6.7-.7.1-1.6,0-2.3.1-.2.1,0,.5-.1,1.1,0,1,0,4.2-.8,4.2-.2,0-.5-.3-.6-.6-.3-1.4,0-3.4,0-5,0-1.9,0-3.8-.2-4.6-.1-.4-.5-1.2-.1-1.5h.1Z"/>
      </svg>
    </div>
  {:else}
    <!-- Light/Dark mode: single color -->
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      version="1.1" 
      viewBox="0 0 18 18"
      width="16"
      height="16"
      fill={currentTheme === 'light' ? '#5856d6' : '#7c7aff'}
      class="logo-svg"
      data-theme={currentTheme}
    >
      <path d="M3.6,3.9c1.3,0,2.9,0,4.2,0,.7,0,2.3-.5,2.3.7,0,.3-.3.5-.6.5-2.2,0-4.6.2-6.8,0-.4,0-.7-.4-.8-.8-.2-.7,1.2-.7,1.5-.4h0ZM6.1,11c-4.2,0-3.7-5.8.7-5.2,3.7.2,3.1,5.6-.5,5.2h-.2ZM3.6,1.6c.7,0,1.5.4,2.3.4.8.1,1.6,0,2.4,0,.8,1.2-1.4,1.5-2.9,1.3-.9,0-2.7-.8-1.9-1.7h0ZM6.3,9.7c2.5,0,1.9-3.4-.6-2.8-1.2.2-1.4,1.8-.5,2.4.2.2.9.2,1,.3h0ZM4.9,13.2c-.1-1.2,1.5-.9,1.6.1.4,1.5-.2,2.3,2,2.1,1,0,6.7-.6,5,1.1-2.3.5-5.4.7-7.6-.3-.6-.8-.3-2.2-.9-3h0ZM11.3,1.1c2.6-.3,1.5,3.8,2,5,.6.4,2.6-.5,2.8.7,0,.4-.3.6-.6.7-.7.1-1.6,0-2.3.1-.2.1,0,.5-.1,1.1,0,1,0,4.2-.8,4.2-.2,0-.5-.3-.6-.6-.3-1.4,0-3.4,0-5,0-1.9,0-3.8-.2-4.6-.1-.4-.5-1.2-.1-1.5h.1Z"/>
    </svg>
  {/if}
</button>

<style>
  /* Theme toggle logo button */
  .theme-toggle-logo {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: all 0.2s ease;
    color: var(--text-primary);
  }
  
  .theme-toggle-logo:hover {
    background: var(--bg-tertiary);
    transform: scale(1.05);
  }
  
  .theme-toggle-logo:active {
    transform: scale(0.95);
  }
  
  .logo-svg {
    transition: all 0.3s ease;
  }
  
  /* Auto mode wrapper */
  .logo-wrapper-auto {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  /* Auto environment indicators */
  .auto-environment {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
  
  .day-indicator {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 14px;
    height: 14px;
    transition: opacity 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .day-indicator svg {
    width: 10px;
    height: 10px;
  }
  
  /* Animate only the sun rays */
  .sun-rays {
    transform-origin: center;
    animation: ray-sparkle 4s ease-in-out infinite;
  }
  
  @keyframes ray-sparkle {
    0%, 100% {
      transform: scale(1);
      opacity: 0.8;
    }
    25% {
      transform: scale(1.3);
      opacity: 1;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.9;
    }
    75% {
      transform: scale(1.2);
      opacity: 1;
    }
  }
  
  .night-indicator {
    position: absolute;
    width: 36px;
    height: 36px;
    transition: opacity 0.3s ease;
    inset: -2px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  /* Subtle animation for night indicator */
  .night-indicator {
    animation: gentle-pulse 4s ease-in-out infinite;
  }
  
  @keyframes gentle-pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }
  
  /* Shooting star animations - one at a time with longer intervals */
  .shooting-star-1 {
    opacity: 0;
    animation: shooting-star-fall 18s ease-out infinite;
    animation-delay: 0s;
  }
  
  .shooting-star-2 {
    opacity: 0;
    animation: shooting-star-fall 18s ease-out infinite;
    animation-delay: 3s;
  }
  
  .shooting-star-3 {
    opacity: 0;
    animation: shooting-star-fall 18s ease-out infinite;
    animation-delay: 6s;
  }
  
  .shooting-star-4 {
    opacity: 0;
    animation: shooting-star-fall 18s ease-out infinite;
    animation-delay: 9s;
  }
  
  .shooting-star-5 {
    opacity: 0;
    animation: shooting-star-fall 18s ease-out infinite;
    animation-delay: 12s;
  }
  
  .shooting-star-6 {
    opacity: 0;
    animation: shooting-star-fall 18s ease-out infinite;
    animation-delay: 15s;
  }
  
  @keyframes shooting-star-fall {
    0%, 80%, 100% {
      transform: translate(0, 0);
      opacity: 0;
    }
    81% {
      transform: translate(0, 0);
      opacity: 1;
    }
    84% {
      transform: translate(-1.5px, 1.5px);
      opacity: 0.95;
    }
    87% {
      transform: translate(-3px, 3px);
      opacity: 0.85;
    }
    90% {
      transform: translate(-5px, 5px);
      opacity: 0.7;
    }
    93% {
      transform: translate(-7px, 7px);
      opacity: 0.5;
    }
    95% {
      transform: translate(-9px, 9px);
      opacity: 0.3;
    }
    97% {
      transform: translate(-11px, 11px);
      opacity: 0.1;
    }
    99% {
      transform: translate(-13px, 13px);
      opacity: 0;
    }
  }
  
  /* Show day indicator in light mode, night in dark mode */
  .day-indicator {
    opacity: 1;
  }
  
  .night-indicator {
    opacity: 0;
  }
  
  @media (prefers-color-scheme: dark) {
    .logo-wrapper-auto .day-indicator {
      opacity: 0;
    }
    
    .logo-wrapper-auto .night-indicator {
      opacity: 1;
    }
  }
  
  /* Also check for explicit theme classes */
  :global(.light-theme) .logo-wrapper-auto .day-indicator {
    opacity: 1;
  }
  
  :global(.light-theme) .logo-wrapper-auto .night-indicator {
    opacity: 0;
  }
  
  :global(.dark-theme) .logo-wrapper-auto .day-indicator {
    opacity: 0;
  }
  
  :global(.dark-theme) .logo-wrapper-auto .night-indicator {
    opacity: 1;
  }
  
  /* Logo in auto mode */
  .logo-wrapper-auto .logo-svg {
    position: relative;
    z-index: 1;
  }
  
  /* Auto mode A badge */
  .auto-mode-badge {
    position: absolute;
    bottom: -4px;
    right: -4px;
    width: 14px;
    height: 14px;
    background: var(--accent-primary);
    border: 1.5px solid var(--bg-primary);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    opacity: 0;
    animation: badge-appear 3s ease-out;
  }
  
  .auto-text {
    font-size: 8px;
    font-weight: 700;
    color: white;
    line-height: 1;
  }
  
  @keyframes badge-appear {
    0% {
      opacity: 0;
      transform: scale(0.5);
    }
    10% {
      opacity: 1;
      transform: scale(1.1);
    }
    20% {
      opacity: 1;
      transform: scale(1);
    }
    90% {
      opacity: 1;
      transform: scale(1);
    }
    100% {
      opacity: 0;
      transform: scale(0.8);
    }
  }
  
  /* Logo rotation animation on click */
  .logo-svg[data-theme="light"] {
    animation: rotate-in 0.3s ease;
  }
  
  .logo-svg[data-theme="dark"] {
    animation: rotate-in 0.3s ease;
  }
  
  .logo-svg[data-theme="auto"] {
    animation: rotate-in 0.3s ease;
  }
  
  @keyframes rotate-in {
    from {
      transform: rotate(-90deg);
      opacity: 0.5;
    }
    to {
      transform: rotate(0deg);
      opacity: 1;
    }
  }
</style>